import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Bell, 
  BellOff,
  Calendar, 
  AlertTriangle, 
  CheckSquare, 
  Sun,
  Award,
  Sparkles,
  Users,
  Check,
  Trash2,
  Settings,
  X,
  Clock,
  Moon
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICATION_ICONS = {
  meeting_prep: Calendar,
  incident_alert: AlertTriangle,
  task_due: CheckSquare,
  leadership_insight: Sun,
  celebration: Award,
  ai_insight: Sparkles,
  staff_update: Users
};

const NOTIFICATION_COLORS = {
  meeting_prep: "text-blue-400 bg-blue-500/20",
  incident_alert: "text-red-400 bg-red-500/20",
  task_due: "text-amber-400 bg-amber-500/20",
  leadership_insight: "text-orange-400 bg-orange-500/20",
  celebration: "text-emerald-400 bg-emerald-500/20",
  ai_insight: "text-violet-400 bg-violet-500/20",
  staff_update: "text-cyan-400 bg-cyan-500/20"
};

const PRIORITY_COLORS = {
  high: "border-l-red-500",
  medium: "border-l-amber-500",
  low: "border-l-zinc-600"
};

export function NotificationCenter() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences`, { withCredentials: true });
      setPreferences(response.data);
    } catch (error) {
      console.error("Error fetching preferences:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchPreferences();
    
    // Poll for new notifications every 60 seconds
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/mark-read/${notificationId}`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, { withCredentials: true });
      setNotifications(notifications.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      setIsOpen(false);
    }
  };

  const updatePreference = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    try {
      await axios.put(`${API}/notifications/preferences`, newPrefs, { withCredentials: true });
    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="notification-bell"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white animate-pulse">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-96 max-h-[500px] overflow-hidden bg-zinc-900 border-zinc-700 p-0"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-zinc-800">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Bell className="w-4 h-4 text-violet-400" />
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={markAllAsRead}
                  className="text-xs h-7 text-muted-foreground hover:text-white"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={() => setShowSettings(true)}
                className="h-7 w-7"
              >
                <Settings className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto max-h-[380px]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <BellOff className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">We'll let you know when something needs your attention</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                const colorClass = NOTIFICATION_COLORS[notification.type] || "text-zinc-400 bg-zinc-500/20";
                const priorityClass = PRIORITY_COLORS[notification.priority] || PRIORITY_COLORS.low;
                
                return (
                  <div
                    key={notification.id}
                    className={`p-3 border-b border-zinc-800 hover:bg-zinc-800/50 cursor-pointer transition-colors border-l-2 ${priorityClass} ${!notification.read ? 'bg-zinc-800/30' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass.split(' ')[1]}`}>
                        <Icon className={`w-4 h-4 ${colorClass.split(' ')[0]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm font-medium ${notification.read ? 'text-zinc-400' : 'text-white'}`}>
                            {notification.title}
                          </p>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!notification.read && (
                              <span className="w-2 h-2 bg-violet-500 rounded-full" />
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                              className="p-1 hover:bg-zinc-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3 text-zinc-500" />
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2 whitespace-pre-line">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] text-zinc-500">
                            {formatTime(notification.created_at)}
                          </span>
                          {notification.action_label && (
                            <span className="text-[10px] text-violet-400">
                              {notification.action_label} →
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t border-zinc-800 text-center">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-muted-foreground"
                onClick={() => {
                  navigate('/dashboard');
                  setIsOpen(false);
                }}
              >
                View All Activity
              </Button>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-zinc-900 border-zinc-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-violet-400" />
              Notification Settings
            </DialogTitle>
          </DialogHeader>
          
          {preferences && (
            <div className="space-y-6 py-4">
              {/* Quiet Hours */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-violet-400" />
                    <Label className="font-medium">Family Time (Quiet Hours)</Label>
                  </div>
                  <Switch
                    checked={preferences.quiet_hours_enabled}
                    onCheckedChange={(v) => updatePreference("quiet_hours_enabled", v)}
                  />
                </div>
                {preferences.quiet_hours_enabled && (
                  <div className="flex items-center gap-3 pl-6">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">From</Label>
                      <Input
                        type="time"
                        value={preferences.quiet_hours_start}
                        onChange={(e) => updatePreference("quiet_hours_start", e.target.value)}
                        className="w-28 h-8 text-sm bg-zinc-800 border-zinc-700"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground">To</Label>
                      <Input
                        type="time"
                        value={preferences.quiet_hours_end}
                        onChange={(e) => updatePreference("quiet_hours_end", e.target.value)}
                        className="w-28 h-8 text-sm bg-zinc-800 border-zinc-700"
                      />
                    </div>
                  </div>
                )}
                <p className="text-xs text-muted-foreground pl-6">
                  No notifications during this time to protect your family time
                </p>
              </div>

              <div className="border-t border-zinc-800" />

              {/* Notification Types */}
              <div className="space-y-3">
                <Label className="font-medium text-sm text-zinc-300">Notification Types</Label>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-400" />
                      <span className="text-sm">Meeting Reminders</span>
                    </div>
                    <Switch
                      checked={preferences.meeting_reminders}
                      onCheckedChange={(v) => updatePreference("meeting_reminders", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-sm">Incident Alerts</span>
                    </div>
                    <Switch
                      checked={preferences.incident_alerts}
                      onCheckedChange={(v) => updatePreference("incident_alerts", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckSquare className="w-4 h-4 text-amber-400" />
                      <span className="text-sm">Task Reminders</span>
                    </div>
                    <Switch
                      checked={preferences.task_reminders}
                      onCheckedChange={(v) => updatePreference("task_reminders", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sun className="w-4 h-4 text-orange-400" />
                      <span className="text-sm">Daily Leadership Insights</span>
                    </div>
                    <Switch
                      checked={preferences.leadership_daily}
                      onCheckedChange={(v) => updatePreference("leadership_daily", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Award className="w-4 h-4 text-emerald-400" />
                      <span className="text-sm">Celebration Alerts</span>
                    </div>
                    <Switch
                      checked={preferences.celebration_alerts}
                      onCheckedChange={(v) => updatePreference("celebration_alerts", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-violet-400" />
                      <span className="text-sm">AI Insights</span>
                    </div>
                    <Switch
                      checked={preferences.ai_insights}
                      onCheckedChange={(v) => updatePreference("ai_insights", v)}
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-800" />

              {/* Sound */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm">Sound Notifications</span>
                </div>
                <Switch
                  checked={preferences.sound_enabled}
                  onCheckedChange={(v) => updatePreference("sound_enabled", v)}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default NotificationCenter;
